import * as React from 'react';
import * as Sentry from '@sentry/nextjs';
import { useReportWebVitals } from 'next/web-vitals';

// Next.js custom metrics that are NOT captured by Sentry
const NEXTJS_CUSTOM_METRICS = ['Next.js-hydration', 'Next.js-route-change-to-render', 'Next.js-render'] as const;

// Core Web Vitals that are already captured by Sentry's BrowserTracing integration
const SENTRY_CAPTURED_METRICS = ['FCP', 'LCP', 'CLS', 'TTFB', 'INP', 'FID'] as const;

/**
 * WebVitals: Reports Next.js custom metrics to Sentry
 *
 * Only captures Next.js-specific metrics (hydration, route-change-to-render, render)
 * that are NOT automatically captured by Sentry's BrowserTracing integration.
 *
 * Core Web Vitals (LCP, CLS, FCP, TTFB, INP) are automatically handled by Sentry
 * and are filtered out to avoid duplication.
 *
 * Usage:
 * Place once at the root of your app (e.g., in Providers component).
 * Do NOT place in multiple components as it will cause duplicate reporting.
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/nextjs/metrics/
 */
export const WebVitals: React.FC = () => {
  useReportWebVitals(metric => {
    const metricName: string = metric.name;
    const enableSentry = process.env.NEXT_PUBLIC_WORK_PERMIT_WEB_VITALS_ENABLED === 'true';
    if (!enableSentry) {
      return;
    }

    // Skip Core Web Vitals - Sentry already captures these via BrowserTracing
    if (SENTRY_CAPTURED_METRICS.includes(metricName as (typeof SENTRY_CAPTURED_METRICS)[number])) {
      return;
    }

    // Only send Next.js custom metrics to Sentry
    if (NEXTJS_CUSTOM_METRICS.includes(metricName as (typeof NEXTJS_CUSTOM_METRICS)[number])) {
      try {
        // Send to Sentry as a distribution metric
        Sentry.metrics.distribution(metricName, metric.value, {
          unit: 'millisecond',
          attributes: {
            rating: metric.rating || 'unknown',
            navigationType: metric.navigationType || 'unknown'
          }
        });
      } catch (error) {
        console.error('[WebVitals] Failed to send metric to Sentry:', error);
      }
    }
  });
  return null;
};